import { useState, useRef } from "react";
import { CheckCircle, ArrowRight, Shield, Clock, Award, DollarSign, CreditCard, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service_type: "",
    stump_count: 1,
    stump_size: "",
    message: "",
  });
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      ['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(file.type)
    );
    
    if (validFiles.length + images.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }
    
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setImages([...images, ...newImages]);
  };

  const removeImage = (index) => {
    const newImages = [...images];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('address', formData.address);
      submitData.append('service_type', formData.service_type);
      submitData.append('stump_count', formData.stump_count.toString());
      submitData.append('stump_size', formData.stump_size);
      submitData.append('message', formData.message);
      
      images.forEach((img) => {
        submitData.append('images', img.file);
      });

      const response = await fetch(`${API}/quotes-with-images`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) throw new Error('Failed to submit');

      toast.success("Quote request submitted! We'll contact you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        service_type: "",
        stump_count: 1,
        stump_size: "",
        message: "",
      });
      setImages([]);
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const trustBadges = [
    { icon: Shield, text: "Licensed & Insured" },
    { icon: Clock, text: "Same-Day Service*" },
    { icon: Award, text: "10+ Years Experience" },
    { icon: CreditCard, text: "Financing Available" },
  ];

  return (
    <section
      id="quote"
      className="relative min-h-screen pt-20 md:pt-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/13790935/pexels-photo-13790935.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
        }}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 noise-bg" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-white animate-slide-in-left">
            <div className="accent-line mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight">
              <span className="text-[#FF4D00]">Stump Grinding</span>{" "}
              <span className="text-[#FF4D00]">Near Me</span><br />
              Tucson, AZ
            </h1>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-6">
              Tree Stump Removal Service | Stump Grinding Tucson AZ
            </h2>
            <p className="font-body text-lg sm:text-xl text-gray-300 mb-6">
              Looking for stump grinding near me in Tucson? We are Southern Arizona's trusted stump removal service — fast, licensed, insured, and thorough. Our tree stump grinding service eliminates stumps from residential and commercial properties across Tucson, Oro Valley, Catalina, and surrounding areas.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 mb-4">
              {trustBadges.map((badge, index) => (
                <div
                  key={index}
                  className="trust-badge rounded-sm"
                  data-testid={`trust-badge-${index}`}
                >
                  <badge.icon className="w-5 h-5 text-[#FF4D00]" />
                  <span className="font-body text-sm font-medium">{badge.text}</span>
                </div>
              ))}
            </div>

            {/* Same-day service footnote */}
            <p className="font-body text-xs text-gray-400 mb-6">
              * Same-day stump grinding available when scheduled with a current tree removal service.
            </p>

            {/* Benefits List */}
            <ul className="space-y-3 mb-6">
              {[
                "Pricing from $300-$850 depending on size",
                "Free estimates with no obligation",
                "Serving Tucson & surrounding areas",
                "Financing available for all budgets",
              ].map((benefit, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#FF4D00] flex-shrink-0" />
                  <span className="font-body text-gray-200">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* Pricing Banner */}
            <div className="bg-white/10 border border-white/20 p-4 mb-6 rounded-sm">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-[#FF4D00]" />
                <div>
                  <span className="font-heading font-bold text-xl text-white">$300 - $850</span>
                  <p className="font-body text-gray-300 text-sm">Based on stump size & accessibility</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="btn-primary px-8 py-4 text-lg rounded-none flex items-center gap-2"
                data-testid="hero-call-btn"
              >
                <a href="tel:+15202007739,2">
                  Call Now: (520) 200-7739 Ext 2
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>

          {/* Right - Quote Form */}
          <div
            className="quote-form p-6 sm:p-8 animate-fade-in-up"
            data-testid="quote-form"
          >
            <div className="text-center mb-6">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] uppercase">
                Get Your Free Quote
              </h2>
              <p className="font-body text-gray-600 mt-2">
                Fill out the form below • Financing available
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="font-body font-semibold text-sm">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder="John Smith"
                    data-testid="quote-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="font-body font-semibold text-sm">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1 rounded-none"
                    placeholder="(520) 555-1234"
                    data-testid="quote-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="font-body font-semibold text-sm">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 rounded-none"
                  placeholder="john@example.com"
                  data-testid="quote-email-input"
                />
              </div>

              <div>
                <Label htmlFor="address" className="font-body font-semibold text-sm">
                  Property Address *
                </Label>
                <Input
                  id="address"
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="mt-1 rounded-none"
                  placeholder="123 Main St, Tucson, AZ"
                  data-testid="quote-address-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="service_type" className="font-body font-semibold text-sm">
                    Service Needed *
                  </Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                    required
                  >
                    <SelectTrigger className="mt-1 rounded-none" data-testid="quote-service-select">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stump-grinding">Stump Grinding</SelectItem>
                      <SelectItem value="stump-removal">Complete Stump Removal</SelectItem>
                      <SelectItem value="root-grinding">Root Grinding</SelectItem>
                      <SelectItem value="multiple">Multiple Services</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="stump_count" className="font-body font-semibold text-sm">
                    Number of Stumps
                  </Label>
                  <Select
                    value={formData.stump_count.toString()}
                    onValueChange={(value) => setFormData({ ...formData, stump_count: parseInt(value) })}
                  >
                    <SelectTrigger className="mt-1 rounded-none" data-testid="quote-count-select">
                      <SelectValue placeholder="Select count" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Stump</SelectItem>
                      <SelectItem value="2">2 Stumps</SelectItem>
                      <SelectItem value="3">3 Stumps</SelectItem>
                      <SelectItem value="4">4 Stumps</SelectItem>
                      <SelectItem value="5">5+ Stumps</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="stump_size" className="font-body font-semibold text-sm">
                  Approximate Stump Size
                </Label>
                <Select
                  value={formData.stump_size}
                  onValueChange={(value) => setFormData({ ...formData, stump_size: value })}
                >
                  <SelectTrigger className="mt-1 rounded-none" data-testid="quote-size-select">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small (under 12")</SelectItem>
                    <SelectItem value="medium">Medium (12" - 24")</SelectItem>
                    <SelectItem value="large">Large (24" - 36")</SelectItem>
                    <SelectItem value="extra-large">Extra Large (36"+)</SelectItem>
                    <SelectItem value="unknown">Not Sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Image Upload */}
              <div>
                <Label className="font-body font-semibold text-sm">
                  Upload Stump Photos (Optional)
                </Label>
                <p className="font-body text-xs text-gray-500 mb-2">
                  Photos help us provide a more accurate quote
                </p>
                <div 
                  className="mt-1 border-2 border-dashed border-gray-300 rounded-sm p-3 text-center cursor-pointer hover:border-[#FF4D00] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                  <p className="font-body text-xs text-gray-600">
                    Click to upload (max 5 images)
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="quote-image-upload"
                />
                
                {/* Image Previews */}
                {images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {images.map((img, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={img.preview} 
                          alt={`Preview ${index + 1}`}
                          className="w-12 h-12 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-2 h-2" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="message" className="font-body font-semibold text-sm">
                  Additional Details
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mt-1 rounded-none"
                  placeholder="Tell us about your project..."
                  rows={3}
                  data-testid="quote-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-4 text-lg rounded-none"
                data-testid="quote-submit-btn"
              >
                {isSubmitting ? "Submitting..." : "Get My Free Quote"}
              </Button>

              <p className="text-center text-sm text-gray-500 font-body">
                No spam. We respect your privacy.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
